/**
 * Safely converts a value into a Set of strings of type T.
 *
 * @template T - The specific string type (e.g., Permission or Role)
 * @param {T[] | T | unknown} value - The value to convert
 * - If array: filters for valid strings and converts to Set
 * - If string: creates a single-item Set
 * - If neither: returns empty Set
 * @returns {Set<T>} A Set containing only valid values of type T
 *
 * @example
 * const permissions = toSet<Permission>(data.permissions);
 * const roles = toSet<Role>('Admin');
 * const empty = toSet<Role>(undefined);
 */
export const toSet = <T extends string>(value: T[] | T | unknown): Set<T> => {
  if (Array.isArray(value)) {
    return new Set(value.filter((v): v is T => typeof v === 'string'));
  }

  if (typeof value === 'string') {
    return new Set([value as T]);
  }

  return new Set<T>();
};

/**
 * Checks if two sets are equal by comparing their size and contents
 */
export const areSetsEqual = <T>(a: Set<T>, b: Set<T>): boolean => {
  if (a.size !== b.size) {
    return false;
  }

  return Array.from(a).every((item) => b.has(item));
};
